// Backend custom stat value structure from JSONB
/**
 *
 */
export interface BackendCustomStatValue {
  value: string;
  description: string;
}

// Backend custom stats collection from PlayerBioData.customStats
/**
 *
 */
export interface BackendCustomStats {
  [key: string]: BackendCustomStatValue;
}

// Organization-specific metric configuration
/**
 *
 */
export interface OrganizationMetricConfig {
  backendKey: string; // Key in customStats JSONB (e.g., "BALL_PLAYING")
  displayName: string; // Organization-specific display name (e.g., "Ball Playing" for QPR)
  category: string; // Grouping category (e.g., "Technical", "Physical")
  displayOrder: number; // Order in which to display metrics
  minValue: number; // Minimum value for filters
  maxValue: number; // Maximum value for filters
  step: number; // Step size for filter sliders
  isDecimal: boolean; // Whether to show decimal values
  isActive?: boolean; // Whether this metric is currently active
  unit?: string; // Optional unit (e.g., "km/h", "%")
}

// Organization custom metrics configuration
/**
 *
 */
export interface OrganizationCustomMetricsConfig {
  enabled: boolean;
  metrics: OrganizationMetricConfig[];
}

// Processed custom metric for UI display
/**
 *
 */
export interface ProcessedCustomMetric {
  id: string; // Same as backendKey for unique identification
  displayName: string; // Organization-specific display name
  value: number; // Parsed numeric value from backend
  description: string; // Description from backend
  category: string; // Category for grouping
  displayOrder: number; // Order for display
  minValue: number; // For filter ranges
  maxValue: number; // For filter ranges
  step: number; // For filter sliders
  isDecimal: boolean; // For value formatting
  unit?: string; // Optional unit display
}

// Hook return type for organization custom metrics
/**
 *
 */
export interface OrganizationCustomMetrics {
  organizationId: string;
  organizationName: string;
  metrics: ProcessedCustomMetric[];
  hasCustomMetrics: boolean;
  metricsByCategory: Map<string, ProcessedCustomMetric[]>;
}

// Filter field name generator for custom metrics
export const getCustomMetricFilterFieldName = (backendKey: string): string => {
  return `custom_${backendKey}`;
};

/**
 * Whether a filter-values object holds a range for any custom metric.
 *
 * Lets a caller that only needs the org's custom-stat list to translate
 * `custom_*` filter keys skip that read when there are none (SE-8832): the
 * read can take 20 s on a cold replica (SE-8829).
 * @param filterValues - Filter values keyed as in `filterValuesVar`
 * @returns True when some `custom_*` key holds a non-null value
 */
export const hasCustomMetricFilter = (
  filterValues: object | null | undefined
): boolean =>
  Object.entries(filterValues ?? {}).some(
    ([key, value]) =>
      key.startsWith(getCustomMetricFilterFieldName("")) && value != null
  );

// Process backend custom stats with organization configuration
export const processCustomMetrics = (
  customStats: BackendCustomStats | null | undefined,
  metricConfigs: OrganizationMetricConfig[]
): ProcessedCustomMetric[] => {
  if (!customStats || !metricConfigs) {
    return [];
  }

  return metricConfigs
    .filter(config => config.isActive !== false)
    .reduce<ProcessedCustomMetric[]>((acc, config) => {
      const backendStat = customStats[config.backendKey];
      if (!backendStat) return acc;

      const numericValue = parseFloat(backendStat.value);
      if (isNaN(numericValue)) return acc;

      return [
        ...acc,
        {
          id: config.backendKey,
          displayName: config.displayName,
          value: numericValue,
          description: backendStat.description,
          category: config.category,
          displayOrder: config.displayOrder,
          minValue: config.minValue,
          maxValue: config.maxValue,
          step: config.step,
          isDecimal: config.isDecimal,
          unit: config.unit,
        },
      ];
    }, [])
    .sort((a, b) => a.displayOrder - b.displayOrder);
};

/**
 * Normalize a string to Title Case (first letter uppercase, rest lowercase per word)
 * Handles ALL CAPS backend keys (e.g., "GOAL THREAT" -> "Goal Threat")
 * @param str - Input string to normalize
 * @returns Title-cased string
 */
const toTitleCase = (str: string): string =>
  str
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

/**
 * Process backend custom stats dynamically without organization config.
 * Normalizes display names to Title Case and deduplicates entries that
 * differ only in casing (e.g., backend keys "CREATIVITY" and "Creativity"
 * both resolve to "Creativity" — the last occurrence wins).
 * @param customStats - Backend custom stats JSONB object
 * @returns Deduplicated array of processed custom metrics
 */
export const processDynamicCustomMetrics = (
  customStats: BackendCustomStats
): ProcessedCustomMetric[] => {
  const conflictingMetrics = ["shot stopping", "passing", "positioning"];

  return Object.entries(customStats)
    .reduce<{
      readonly metrics: readonly ProcessedCustomMetric[];
      readonly seen: Record<string, true>;
    }>(
      (acc, [backendKey, statValue], index) => {
        const numericValue = parseFloat(statValue.value);
        if (isNaN(numericValue)) return acc;

        // Convert BACKEND_KEY to Title Case display name
        // (e.g., "GOAL_THREAT" -> "Goal Threat", "Ball_Playing" -> "Ball Playing")
        const baseName = backendKey.split("_").join(" ");
        const titleCaseName = toTitleCase(baseName);

        // Add prefix for metrics that conflict with built-in metric names
        const displayName = conflictingMetrics.includes(
          titleCaseName.toLowerCase()
        )
          ? `Custom ${titleCaseName}`
          : titleCaseName;

        // Deduplicate: backend may have both "CREATIVITY" and "Creativity" keys
        const normalizedKey = displayName.toLowerCase();
        if (acc.seen[normalizedKey]) return acc;

        return {
          metrics: [
            ...acc.metrics,
            {
              id: backendKey,
              displayName,
              value: numericValue,
              description: statValue.description,
              category: "Custom",
              displayOrder: index,
              minValue: 0,
              maxValue: 100,
              step: 0.1,
              isDecimal: true,
            },
          ],
          seen: { ...acc.seen, [normalizedKey]: true as const },
        };
      },
      { metrics: [], seen: {} }
    )
    .metrics.slice()
    .sort((a, b) => a.displayOrder - b.displayOrder);
};

// Group processed metrics by category
export const groupMetricsByCategory = (
  metrics: ProcessedCustomMetric[]
): Map<string, ProcessedCustomMetric[]> => {
  return metrics.reduce((metricsByCategory, metric) => {
    const category = metric.category;
    const existingMetrics = metricsByCategory.get(category) || [];
    return new Map(metricsByCategory).set(category, [
      ...existingMetrics,
      metric,
    ]);
  }, new Map<string, ProcessedCustomMetric[]>());
};

// Format metric value for display
export const formatCustomMetricValue = (
  value: number,
  isDecimal: boolean,
  unit?: string
): string => {
  // Always format as whole number for custom metrics
  const formattedValue = Math.round(value).toString();
  return unit ? `${formattedValue} ${unit}` : formattedValue;
};
